import { supabase } from "@/lib/supabase";
import { readWorkspaceOrgIdFromStorage } from "@/lib/workspaceOrgStorage";
import type { DocCategoryListResponse } from "@/lib/document/categoryTypes";

type FetchResult<T> =
  | { ok: true; status: number; data: T }
  | { ok: false; status: number; error: string; body?: unknown };

export async function documentFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<FetchResult<T>> {
  if (!supabase) return { ok: false, status: 503, error: "App is not configured for sign-in." };
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { ok: false, status: 401, error: "Sign in required." };

  const headers: HeadersInit = {
    Authorization: `Bearer ${token}`,
    ...(init.headers || {}),
  };
  const ws = readWorkspaceOrgIdFromStorage();
  if (ws) (headers as Record<string, string>)["X-Workspace-Organization-Id"] = ws;

  const r = await fetch(path, { ...init, headers });
  const text = await r.text();
  let json: unknown = null;
  try {
    json = JSON.parse(text) as T;
  } catch {
    return {
      ok: false,
      status: r.status,
      error: text || `Request failed (${r.status})`,
      body: text,
    };
  }

  if (!r.ok) {
    const err =
      json && typeof json === "object" && "error" in json && typeof (json as { error: unknown }).error === "string"
        ? (json as { error: string }).error
        : `Request failed (${r.status})`;
    return { ok: false, status: r.status, error: err, body: json };
  }

  return { ok: true, status: r.status, data: json as T };
}

export async function listDocCategories() {
  return documentFetch<DocCategoryListResponse>("/api/doc/category-list", { method: "GET" });
}

export async function upsertDocCategory(body: {
  id?: string;
  name: string;
  parent_id?: string | null;
  category_key?: string;
  sort_order?: number;
}) {
  return documentFetch<{ ok: true; category: DocCategoryListResponse["categories"][number] }>(
    "/api/doc/category-upsert",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}

export async function deleteDocCategory(id: string) {
  return documentFetch<{ ok: true; deleted_id: string }>("/api/doc/category-delete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id }),
  });
}
